<template>
  <pu-booking-link
    v-bind="bookingLinkParams"
    class="button primary asblock booking-button"
    data-test="booking-button"
    :disabled="disabled"
    :class="{ disabled }"
    @click.native="handleClick"
  >
    <translate>Book now</translate>
    <span class="button-subtext">{{ pitchtypeName }}</span>
  </pu-booking-link>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useNuxtApp, preloadRouteComponents } from 'nuxt/app'
import { useRouter } from 'vue-router'
import type { Party } from '../../types'

interface Props {
  campsiteSlug: string
  dateRange?: { start?: string; end?: string }
  chargetypeId: string
  pitchtypeName: string
  pitchtypeId: string
  party: Party
  disabled?: boolean
  categoryIds?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  dateRange: () => ({ start: undefined, end: undefined }),
  disabled: false,
  categoryIds: () => [],
})

const { $sentry } = useNuxtApp()
const router = useRouter()

// Preload booking page chunks during idle time so the click is instant
onMounted(() => {
  requestIdleCallback(() => {
    void preloadRouteComponents(`/campsite/${props.campsiteSlug}/book/`, router)
  })
})

const bookingLinkParams = computed(() => ({
  campsiteSlug: props.campsiteSlug,
  categoryIds: props.categoryIds,
  dateRange: props.dateRange,
  chargetypeId: props.chargetypeId,
  pitchtypeId: props.pitchtypeId,
  party: props.party,
}))

function validateBookingParams() {
  const missingParams: string[] = []

  if (!props.pitchtypeId) missingParams.push('pitchtypeId')
  if (!props.chargetypeId) missingParams.push('chargetypeId')
  if (!props.campsiteSlug) missingParams.push('campsiteSlug')
  if (!props.dateRange?.start || !props.dateRange?.end) {
    missingParams.push('dateRange')
  }

  return missingParams
}

function handleClick(event: Event) {
  // TODO: Add proper types for window.LUX
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  if ((window as any).LUX && (window as any).LUX.addData) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    ;(window as any).LUX.addData('booking_link_click', true)
  }

  const missingParams = validateBookingParams()

  if (missingParams.length > 0) {
    // Prevent the link from navigating
    event.preventDefault()
    event.stopPropagation()
    if ($sentry) {
      // Report to Sentry when booking link is clicked with missing params
      $sentry.captureException(
        new Error('Booking link clicked with missing parameters'),
        {
          tags: {
            component: 'PitchtypeBookingLink',
            action: 'click',
          },
          contexts: {
            bookingLinkContext: {
              missingParams,
              providedParams: {
                campsiteSlug: props.campsiteSlug,
                pitchtypeId: props.pitchtypeId,
                chargetypeId: props.chargetypeId,
                hasDateRange: !!(
                  props.dateRange?.start && props.dateRange?.end
                ),
                party: props.party,
                disabled: props.disabled,
              },
              timestamp: new Date().toISOString(),
            },
          },
          fingerprint: ['booking-link-missing-params', ...missingParams.sort()],
        },
      )
    }
    return
  }

  // Let pu-booking-link handle the navigation
}
</script>

<style scoped>
.button-subtext {
  display: -webkit-box;
  font-size: var(--small);
  overflow: hidden;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  margin: 0;
  padding: 0.2em 0 0.1em;
}

.button.booking-button {
  padding: 0.5em 1em 0.3em;
}

.disabled {
  pointer-events: none;
  background: var(--colour-page-background);
  box-shadow: 0 0.175em var(--colour-page-background);
}
</style>
